import { motion } from 'framer-motion';
import { Check, Link2, MoreHorizontal, Pencil, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Input } from '../../ui/input';

interface ThreadItemProps {
  id: string;
  title: string;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onDelete?: (id: string) => void;
  onRename?: (id: string, newTitle: string) => void;
  onCopyLink?: (id: string) => void;
}

export const ThreadItem = ({
  id,
  title,
  isSelected,
  onSelect,
  onDelete,
  onRename,
  onCopyLink,
}: ThreadItemProps) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState(title);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [displayTitle, setDisplayTitle] = useState(title);
  const [isHovered, setIsHovered] = useState(false);
  const prevTitleRef = useRef(title);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync display title when prop changes
  useEffect(() => {
    if (title !== prevTitleRef.current) {
      setDisplayTitle(title);
      setNewTitle(title);
      prevTitleRef.current = title;
    }
  }, [title]);

  // Focus input when entering rename mode
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const handleRename = () => {
    if (onRename && newTitle.trim() !== '' && newTitle !== title) {
      onRename(id, newTitle.trim());
    }
    setIsRenaming(false);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(id);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleRename();
    } else if (e.key === 'Escape') {
      setIsRenaming(false);
      setNewTitle(title);
    }
  };

  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNewTitle(title);
    setIsRenaming(true);
  };

  const openDeleteDialog = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleteDialogOpen(true);
  };

  // Rename mode
  if (isRenaming) {
    return (
      <motion.div
        initial={{ scale: 0.98, opacity: 0.8 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        className={cn(
          'relative rounded-lg p-2',
          'bg-white',
          'border border-slate-200/80',
          'shadow-sm',
        )}
      >
        <Input
          ref={inputRef}
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'h-7 text-xs font-medium',
            'bg-slate-50/50',
            'border-slate-200/80 focus:border-slate-300',
            'focus:ring-1 focus:ring-slate-900/5',
            'rounded-md',
            'placeholder:text-slate-400',
          )}
          placeholder="Thread name..."
        />
        <div className="flex justify-end gap-1.5 mt-2">
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={(e) => {
              e.stopPropagation();
              setIsRenaming(false);
              setNewTitle(title);
            }}
            className={cn(
              'h-6 px-2 rounded-md',
              'text-[11px] font-medium text-slate-500',
              'hover:text-slate-700 hover:bg-slate-100',
              'transition-colors duration-150',
              'flex items-center gap-1',
            )}
          >
            <X className="w-3 h-3" />
            Cancel
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={(e) => {
              e.stopPropagation();
              handleRename();
            }}
            className={cn(
              'h-6 px-2 rounded-md',
              'text-[11px] font-medium text-white',
              'bg-slate-800',
              'hover:bg-slate-700',
              'transition-colors duration-150',
              'flex items-center gap-1',
            )}
          >
            <Check className="w-3 h-3" />
            Save
          </motion.button>
        </div>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        whileTap={{ scale: 0.99 }}
        onClick={() => onSelect(id)}
        className={cn(
          'group relative rounded-lg cursor-pointer',
          'transition-all duration-150 ease-out',
          isSelected
            ? 'bg-white border border-slate-200/80 shadow-sm'
            : 'bg-transparent border border-transparent hover:bg-white/80 hover:border-slate-100',
        )}
      >
        {/* Selected indicator */}
        <motion.div
          initial={false}
          animate={{
            scaleY: isSelected ? 1 : 0,
            opacity: isSelected ? 1 : 0,
          }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          className={cn(
            'absolute left-0 top-1/2 -translate-y-1/2',
            'w-0.5 h-4 rounded-full',
            'bg-slate-700',
          )}
        />

        <div className="flex items-center justify-between px-2 py-1.5">
          {/* Title */}
          <div className="flex-1 min-w-0 pl-1">
            <p
              className={cn(
                'text-xs font-medium truncate',
                'transition-colors duration-150',
                isSelected
                  ? 'text-slate-800'
                  : 'text-slate-600 group-hover:text-slate-700',
              )}
            >
              {displayTitle}
            </p>
          </div>

          {/* Actions */}
          <motion.div
            initial={false}
            animate={{
              opacity: isHovered || isSelected ? 1 : 0,
              x: isHovered || isSelected ? 0 : 4,
            }}
            transition={{ duration: 0.1 }}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  className={cn(
                    'p-1 rounded-md',
                    'text-slate-400 hover:text-slate-600',
                    'hover:bg-slate-100',
                    'transition-colors duration-100',
                    'focus:outline-none',
                  )}
                >
                  <MoreHorizontal className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={2}
                className={cn(
                  'w-28 p-0.5 z-[200]',
                  'bg-white',
                  'border border-slate-200/80',
                  'shadow-md',
                  'rounded-lg',
                )}
              >
                <DropdownMenuItem
                  onClick={handleRenameClick}
                  className={cn(
                    'flex items-center gap-1.5 px-2 py-1.5',
                    'text-xs text-slate-600',
                    'rounded-md cursor-pointer',
                    'focus:bg-slate-50 focus:text-slate-800',
                  )}
                >
                  <Pencil className="w-3 h-3" />
                  Rename
                </DropdownMenuItem>
                {onCopyLink && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onCopyLink(id);
                    }}
                    className={cn(
                      'flex items-center gap-1.5 px-2 py-1.5',
                      'text-xs text-slate-600',
                      'rounded-md cursor-pointer',
                      'focus:bg-slate-50 focus:text-slate-800',
                    )}
                  >
                    <Link2 className="w-3 h-3" />
                    Copy link
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={openDeleteDialog}
                  className={cn(
                    'flex items-center gap-1.5 px-2 py-1.5',
                    'text-xs text-red-600',
                    'rounded-md cursor-pointer',
                    'focus:bg-red-50 focus:text-red-700',
                  )}
                >
                  <Trash2 className="w-3 h-3" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </motion.div>
        </div>
      </motion.div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md z-[300]">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Delete thread</DialogTitle>
            <DialogDescription className="text-slate-500">
              Are you sure you want to delete &ldquo;{title}&rdquo;? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              className="border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
